// ============================================================================
// Partage de compte / bascule entre comptes (membres d'équipe)
// ============================================================================
// - `user` (localStorage)          = SESSION ACTIVE (peut être un compte partagé/délégué)
// - `peelo_identity` (localStorage) = MON identité réelle (mon login), jamais écrasée par
//   une session déléguée. Sert à revenir à mon compte et à autoriser les bascules.
//
// Les appels de bascule (/switch, /my-accounts) sont TOUJOURS autorisés par le jeton de
// MON identité, jamais par le jeton délégué : on court-circuite donc l'intercepteur axios
// de l'app (qui injecte le jeton de la session active) via une instance axios brute.
// ============================================================================

import rawAxios from 'axios';
import { apiBaseUrl } from '@/config';
import { setSsoCookie } from '@/utils/ssoCookie';

const IDENTITY_KEY = 'peelo_identity';

function parse(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (_) { return null; }
}

export function getActiveUser() { return parse('user'); }

export function getActiveToken() {
  const u = getActiveUser();
  return (u && (u.data ? u.data.token : u.token)) || null;
}

export function decodeJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(json);
  } catch (_) { return null; }
}

// Renvoie l'objet _delegated si la session active est un accès partagé, sinon null.
export function getDelegation() {
  const tok = getActiveToken();
  if (!tok) return null;
  const payload = decodeJwt(tok);
  return (payload && payload._delegated) || null;
}

export function isDelegated() { return !!getDelegation(); }

export function getIdentity() { return parse(IDENTITY_KEY); }

export function getIdentityToken() {
  const id = getIdentity();
  return (id && (id.data ? id.data.token : id.token)) || null;
}

// Libellé du compte actuellement actif (nom + email).
export function getActiveAccountLabel() {
  const u = getActiveUser();
  const d = (u && u.data) ? u.data : u || {};
  return { name: d.name || 'Mon compte', email: d.email || '' };
}

// Capture mon identité depuis la session active — UNIQUEMENT si cette session n'est pas
// déjà déléguée (sinon on écraserait mon identité par celle du compte partagé).
export function ensureIdentity() {
  if (isDelegated()) return;            // session active = compte partagé -> ne rien toucher
  const active = getActiveUser();
  if (active && (active.data ? active.data.token : active.token)) {
    localStorage.setItem(IDENTITY_KEY, JSON.stringify(active));
  }
}

export function clearIdentity() { localStorage.removeItem(IDENTITY_KEY); }

// Appel API brut autorisé par un jeton explicite (contourne l'intercepteur de l'app).
function apiCall(method, path, token, data) {
  return rawAxios({
    method,
    url: `${apiBaseUrl}${path}`,
    data,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    validateStatus: () => true
  });
}

// Comptes partagés auxquels j'ai accès (autorisé par MON identité).
export async function listSharedAccounts() {
  ensureIdentity();
  const token = getIdentityToken();
  if (!token) return [];
  const r = await apiCall('get', '/strong/team/my-accounts', token);
  return (r.status === 200 && r.data && r.data.accounts) || [];
}

// Agents partagés AVEC MOI (invitations limitées à des agents), aplatis pour la
// section « Partagés avec moi » de ma liste d'agents. Vide en session déléguée :
// cette section n'a de sens que dans MON propre compte.
export async function listSharedAgents() {
  if (isDelegated()) return [];
  const accounts = await listSharedAccounts();
  const out = [];
  for (const a of accounts) {
    if ((a.scope || 'account') !== 'chatbots') continue;
    for (const b of (a.chatbots || [])) {
      out.push({ account_id: a.account_id, account_name: a.name, account_email: a.email, role: a.role, bot: b });
    }
  }
  return out;
}

// Construit l'objet session active à partir d'un jeton délégué + infos du compte cible.
function buildDelegatedUser(token, account) {
  const p = decodeJwt(token) || {};
  return {
    data: {
      id: account.id,
      name: account.name,
      email: account.email,
      tel: p.tel || '',
      profile_picture: p.profile_picture || 'https://placehold.co/250',
      provenance: p.provenance || 'delegated',
      subscription: p.subscription || null,
      pointing_chatbot_stats: p.pointing_chatbot_stats || null,
      token,
      delegated: !account.own,
      acting_role: account.role || null,
      // Portée de l'accès partagé : 'account' (tout) ou 'chatbots' (agents listés).
      acting_scope: account.scope || 'account',
      acting_scope_names: account.chatbot_names || []
    }
  };
}

// True si la session active est un accès partagé LIMITÉ à certains agents.
export function isScopedSession() {
  const d = getDelegation();
  return !!(d && d.scope === 'chatbots');
}

// Libellé de la portée de la session active ("Tout le compte" / noms des agents).
export function getScopeLabel() {
  const u = getActiveUser(); const d = (u && (u.data || u)) || {};
  const names = d.acting_scope_names || [];
  if ((d.acting_scope || 'account') !== 'chatbots' || !names.length) return null;
  return names.length === 1 ? `l'agent « ${names[0]} »` : `${names.length} agents (${names.join(', ')})`;
}

// Basculer vers un compte (partagé ou le mien).
export async function switchTo(accountId) {
  ensureIdentity();
  const identityId = (() => { const i = getIdentity(); const d = i && (i.data || i); return d && d.id; })();

  // Retour à mon propre compte : on restaure directement mon identité.
  if (identityId && accountId === identityId) return switchBack();

  const token = getIdentityToken();
  const r = await apiCall('post', '/strong/team/switch', token, { account_id: accountId });
  if (r.status !== 200 || !r.data || !r.data.token) {
    const msg = (r.data && r.data.message) || "Impossible de basculer vers ce compte.";
    throw new Error(msg);
  }
  const activeUser = buildDelegatedUser(r.data.token, r.data.account);
  localStorage.setItem('user', JSON.stringify(activeUser));
  setSsoCookie(r.data.token);
  // Accès limité à des agents : on atterrit sur la liste d'agents (le hub admin
  // du compte hôte est hors périmètre) ; accès compte entier : hub classique.
  window.location.href = (r.data.account && r.data.account.scope === 'chatbots')
    ? '/dashboard/chatbots' : '/dashboard';
}

// Revenir à mon compte (restaure l'identité sans appel serveur).
export function switchBack() {
  const identity = getIdentity();
  if (!identity) { window.location.href = '/dashboard'; return; }
  localStorage.setItem('user', JSON.stringify(identity));
  const tok = identity.data ? identity.data.token : identity.token;
  if (tok) setSsoCookie(tok);
  window.location.href = '/dashboard';
}

// ---- Gestion de l'équipe (côté propriétaire, session active) --------------
// scope: 'account' (défaut) ou 'chatbots' avec la liste des ids d'agents partagés.
export async function inviteMember(email, role, scope = 'account', chatbotIds = []) {
  const r = await apiCall('post', '/strong/team/invite', getActiveToken(), {
    email, role, scope, chatbot_ids: chatbotIds, base_url: window.location.origin
  });
  if (r.status >= 200 && r.status < 300) return r.data;
  throw new Error((r.data && r.data.message) || "Échec de l'invitation.");
}

export async function listMembers() {
  const r = await apiCall('get', '/strong/team/members', getActiveToken());
  return (r.status === 200 && r.data && r.data.members) || [];
}

export async function revokeMember(id) {
  const r = await apiCall('delete', `/strong/team/members/${id}`, getActiveToken());
  if (r.status >= 200 && r.status < 300) return true;
  throw new Error((r.data && r.data.message) || 'Échec de la révocation.');
}

export async function updateMemberRole(id, role) {
  const r = await apiCall('patch', `/strong/team/members/${id}`, getActiveToken(), { role });
  if (r.status >= 200 && r.status < 300) return true;
  throw new Error((r.data && r.data.message) || 'Échec de la mise à jour du rôle.');
}

export async function getInviteInfo(token) {
  const r = await apiCall('get', `/strong/team/invite/${token}`);
  if (r.status === 200 && r.data) return r.data.invite;
  return null;
}

export async function acceptInvite(token) {
  const r = await apiCall('post', '/strong/team/accept', getActiveToken(), { token });
  return r; // le composant gère les cas (200 / 403 wrong_account / 410 …)
}
